import { TOOLBAR_BUTTON_SX } from "@/controls/buttons/toolbarButton";
import { ResetFiltersButton } from "@/controls/buttons/ResetFiltersButton";
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	MenuItem,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { Moment } from "moment";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import type {
	Crew,
	ProductSize,
	ProductType,
} from "../models/InformationRetrieval";
import { ALL_SHIFTS, CURRENT_SHIFT } from "../utils/filterOptions";

interface Props {
	start: Moment;
	end: Moment;
	onStartChange: (date: Moment | null) => void;
	onEndChange: (date: Moment | null) => void;
	productTypeId: number | null;
	productSizeId: number | null;
	shiftId: string;
	groupByDate: boolean;
	groupByShift: boolean;
	onProductTypeChange: (id: number | null) => void;
	onProductSizeChange: (id: number | null) => void;
	onShiftChange: (id: string) => void;
	onGroupByDateChange: (value: boolean) => void;
	onGroupByShiftChange: (value: boolean) => void;
	productTypes: ProductType[];
	productSizes: ProductSize[];
	crews: Crew[];
	/** Offered only when the latest crew rolled in the selected dates. */
	canUseCurrentShift: boolean;
	hasProduction: boolean;
	isLoadingOptions: boolean;
	onReset: () => void;
	onExportExcel: () => void;
	/** Leave out to hide Build Recipe (the straightener page has none). */
	onBuildRecipe?: () => void;
	/** False while the report has no rows to build a recipe from. */
	canBuildRecipe?: boolean;
	/** Hint when nothing ran in the dates; `{range}` is replaced by the dates. */
	noProductionText?: string;
	/** Tooltip of an enabled Build Recipe button. */
	buildRecipeHint?: string;
	/** Tooltip when Build Recipe is disabled although a product is chosen. */
	buildRecipeDisabledHint?: string;
	canReset: boolean;
}

export const InformationRetrievalFilters = ({
	start,
	end,
	onStartChange,
	onEndChange,
	productTypeId,
	productSizeId,
	shiftId,
	groupByDate,
	groupByShift,
	onProductTypeChange,
	onProductSizeChange,
	onShiftChange,
	onGroupByDateChange,
	onGroupByShiftChange,
	productTypes,
	productSizes,
	crews,
	canUseCurrentShift,
	hasProduction,
	isLoadingOptions,
	onReset,
	onExportExcel,
	onBuildRecipe,
	canBuildRecipe = false,
	noProductionText = "Nothing was rolled between {range}. Widen the date range.",
	buildRecipeHint = "Build a recipe from the R-Factor these billets actually ran at",
	buildRecipeDisabledHint = "Not available while the report reads the legacy database",
	canReset,
}: Props) => {
	// Every filter offers only what ran with the others, so an empty range is a
	// statement about production, not a broken screen.
	const rangeLabel = `${start.format("MM/DD/YYYY")} and ${end.format("MM/DD/YYYY")}`;
	const nothingRolled = !isLoadingOptions && !hasProduction;
	const nothingRolledHint = nothingRolled
		? noProductionText.replace("{range}", rangeLabel)
		: "";

	// The legacy screen gates both actions on a product being chosen: a recipe
	// belongs to one product, and the report is meaningless across all of them.
	const hasProduct = productSizeId != null;

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				px: 1.5,
				py: 0.75,
				flexWrap: "wrap",
			}}
		>
			<Box sx={{ minWidth: 150, maxWidth: 170 }}>
				<DatePicker
					label="Start"
					format="MM/DD/YYYY"
					value={start}
					maxDate={end}
					onChange={onStartChange}
					slotProps={{ textField: { size: "small", fullWidth: true } }}
				/>
			</Box>
			<Box sx={{ minWidth: 150, maxWidth: 170 }}>
				<DatePicker
					label="End"
					format="MM/DD/YYYY"
					value={end}
					minDate={start}
					onChange={onEndChange}
					slotProps={{ textField: { size: "small", fullWidth: true } }}
				/>
			</Box>

			{/* A disabled MUI input swallows pointer events, so each tooltip hangs
			    off a wrapper or it never fires when the explanation is needed. */}
			<Tooltip title={nothingRolledHint}>
				<Box sx={{ width: 150 }}>
					<TextField
						select
						size="small"
						label="Shift"
						fullWidth
						disabled={nothingRolled}
						value={shiftId}
						onChange={(e) => onShiftChange(e.target.value)}
					>
						<MenuItem value={ALL_SHIFTS}>All shifts</MenuItem>
						{canUseCurrentShift ? (
							<MenuItem value={CURRENT_SHIFT}>Current shift</MenuItem>
						) : null}
						{crews.map((c) => (
							<MenuItem key={c.Id} value={c.Id}>
								{c.Id}
							</MenuItem>
						))}
					</TextField>
				</Box>
			</Tooltip>

			{/* Typed rather than scrolled, same control as the R-Factor per Stand
			    and Product Changes reports. Product Type is optional: clearing it
			    means All. Product is required, because the report compares
			    against that product's recipe. */}
			<Tooltip title={nothingRolledHint}>
				<Box sx={{ width: 180 }}>
					<Autocomplete
						size="small"
						options={productTypes}
						value={productTypes.find((t) => t.Id === productTypeId) ?? null}
						getOptionLabel={(option) => option.Name}
						isOptionEqualToValue={(option, selected) =>
							option.Id === selected.Id
						}
						onChange={(_event, option) =>
							onProductTypeChange(option?.Id ?? null)
						}
						disabled={nothingRolled}
						noOptionsText="No items"
						autoHighlight
						renderInput={(params) => (
							<TextField
								{...params}
								label="Product Type"
								placeholder="All"
								slotProps={{ inputLabel: { shrink: true } }}
							/>
						)}
					/>
				</Box>
			</Tooltip>

			<Tooltip title={nothingRolledHint}>
				<Box sx={{ width: 240 }}>
					<Autocomplete
						size="small"
						options={productSizes}
						value={productSizes.find((s) => s.Id === productSizeId) ?? null}
						getOptionLabel={(option) => option.Name}
						isOptionEqualToValue={(option, selected) =>
							option.Id === selected.Id
						}
						onChange={(_event, option) =>
							onProductSizeChange(option?.Id ?? null)
						}
						disabled={nothingRolled}
						noOptionsText="No items"
						autoHighlight
						renderOption={(props, option) => {
							const { key, ...rest } = props as typeof props & { key: string };
							return (
								<li key={option.Id} {...rest}>
									{option.Name}
								</li>
							);
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Product"
								placeholder="Search product..."
								slotProps={{ inputLabel: { shrink: true } }}
							/>
						)}
					/>
				</Box>
			</Tooltip>

			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
				<Typography variant="body2" sx={{ fontWeight: 600 }}>
					Group by
				</Typography>
				<FormControlLabel
					sx={{ mr: 0 }}
					control={
						<Checkbox
							size="small"
							checked={groupByDate}
							onChange={(e) => onGroupByDateChange(e.target.checked)}
						/>
					}
					label="Date"
					slotProps={{ typography: { fontSize: "0.85rem" } }}
				/>
				<FormControlLabel
					sx={{ mr: 0 }}
					control={
						<Checkbox
							size="small"
							checked={groupByShift}
							onChange={(e) => onGroupByShiftChange(e.target.checked)}
						/>
					}
					label="Shift"
					slotProps={{ typography: { fontSize: "0.85rem" } }}
				/>
			</Box>

			<ResetFiltersButton onClick={onReset} disabled={!canReset} />

			<Box sx={{ flexGrow: 1, minWidth: 8 }} />

			{/* The recipe is always built from MILL billets, whatever the screen is
			    reading. In legacy mode that is a different set of billets from the
			    ones on display, so the action is locked rather than silently
			    writing a recipe nobody can trace back to this screen. */}
			{onBuildRecipe ? (
				<Tooltip
					title={
						!hasProduct
							? "Select a Product first"
							: !canBuildRecipe
								? buildRecipeDisabledHint
								: buildRecipeHint
					}
				>
					<span>
						<Button
							variant="contained"
							size="medium"
							disabled={!hasProduct || !canBuildRecipe}
							onClick={onBuildRecipe}
							sx={TOOLBAR_BUTTON_SX}
						>
							Build Recipe
						</Button>
					</span>
				</Tooltip>
			) : null}

			<Box sx={{ width: 190 }}>
				<ExcelButton handleExportToExcel={onExportExcel} />
			</Box>
		</Box>
	);
};
